/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import {
    autoUpdate,
    computePosition,
    flip,
    offset,
    shift,
} from '@floating-ui/dom';

/*
 * Word suggestions for the search field.
 *
 * Replaces jQuery UI's autocomplete, which was the last thing in the Map
 * Viewer that needed jQuery UI. It follows the ARIA combobox pattern: the input
 * keeps focus throughout and announces the active option through
 * `aria-activedescendant`, rather than focus moving into the list — a
 * reader typing a query should not have to tab back to carry on typing.
 *
 * Floating UI positions the list, so it flips above the field when there is
 * no room below instead of running off the bottom of a short window.
 */

let counter = 0;

/**
 * Attach a suggestion list to a text input.
 *
 * @param {HTMLInputElement} input - The field to suggest for.
 * @param {object} options
 * @param {function(string): Promise<string[]>} options.source - Fetches
 *   suggestions for a term.
 * @param {number} [options.minLength] - Shortest term worth asking about.
 * @param {number} [options.delay] - Quiet period before asking, in ms.
 * @param {function(string): void} [options.onSelect] - Called with the
 *   chosen suggestion.
 * @param {HTMLElement} [options.appendTo] - Where to put the list.
 * @returns {object} A controller with `close` and `destroy`.
 */
export function createCombobox(input, options = {}) {
    const {
        source,
        minLength = 3,
        delay = 500,
        onSelect,
        appendTo = document.body,
    } = options;

    const listId = `combobox-list-${(counter += 1)}`;
    const list = document.createElement('ul');
    list.id = listId;
    list.className = 'combobox-suggestions';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    appendTo.appendChild(list);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', 'off');

    let items = [];
    let activeIndex = -1;
    let timer = null;
    let stopAutoUpdate = null;
    let requestId = 0;

    function position() {
        return computePosition(input, list, {
            placement: 'bottom-start',
            middleware: [offset(4), flip(), shift({ padding: 4 })],
        }).then(({ x, y }) => {
            list.style.position = 'absolute';
            list.style.left = `${x}px`;
            list.style.top = `${y}px`;
        });
    }

    function setActive(index) {
        activeIndex = index;
        [...list.children].forEach((option, i) => {
            const active = i === index;
            option.classList.toggle('is-active', active);
            option.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (index >= 0) {
            input.setAttribute(
                'aria-activedescendant',
                list.children[index].id
            );
            list.children[index].scrollIntoView?.({ block: 'nearest' });
        } else {
            input.removeAttribute('aria-activedescendant');
        }
    }

    function close() {
        list.hidden = true;
        list.replaceChildren();
        items = [];
        setActive(-1);
        input.setAttribute('aria-expanded', 'false');
        stopAutoUpdate?.();
        stopAutoUpdate = null;
    }

    function choose(index) {
        const value = items[index];
        if (value === undefined) return;
        input.value = value;
        close();
        onSelect?.(value);
    }

    function render(suggestions) {
        items = suggestions;
        if (!items.length) {
            close();
            return;
        }
        list.replaceChildren(
            ...items.map((text, i) => {
                const option = document.createElement('li');
                option.id = `${listId}-option-${i}`;
                option.setAttribute('role', 'option');
                option.setAttribute('aria-selected', 'false');
                option.textContent = text;
                option.addEventListener('mousedown', (event) => {
                    // `mousedown`, not `click`: the input would blur first
                    // and the list would be gone before the click landed.
                    event.preventDefault();
                    choose(i);
                });
                return option;
            })
        );
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        setActive(-1);
        position();
        stopAutoUpdate?.();
        stopAutoUpdate = autoUpdate(input, list, position);
    }

    async function suggest(term) {
        const id = ++requestId;
        try {
            const suggestions = await source(term);
            // A slower earlier request must not overwrite a later one.
            if (id !== requestId) return;
            render(Array.isArray(suggestions) ? suggestions : []);
        } catch (error) {
            console.error(`Could not suggest words for "${term}".`, error);
            if (id === requestId) close();
        }
    }

    function onInput() {
        window.clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < minLength) {
            close();
            return;
        }
        timer = window.setTimeout(() => suggest(term), delay);
    }

    function onKeyDown(event) {
        if (list.hidden) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((activeIndex + 1) % items.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((activeIndex - 1 + items.length) % items.length);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            choose(activeIndex);
        } else if (event.key === 'Escape') {
            close();
        }
    }

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeyDown);
    input.addEventListener('blur', () => close());

    return {
        close,
        /** The suggestion list element. */
        get element() {
            return list;
        },
        destroy() {
            window.clearTimeout(timer);
            close();
            input.removeEventListener('input', onInput);
            input.removeEventListener('keydown', onKeyDown);
            list.remove();
            for (const attr of [
                'role',
                'aria-expanded',
                'aria-controls',
                'aria-autocomplete',
                'aria-activedescendant',
            ]) {
                input.removeAttribute(attr);
            }
        },
    };
}

export default createCombobox;
